import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { type Dispatch, type UnknownAction } from "redux";
import { Trash2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import {
  addAggregatorCreationRequest as addAggregatorCreationRequestAction,
  addAggregatorCreationRequestReset as addAggregatorCreationRequestResetAction,
  aggregatorCreationRequestDetails as aggregatorCreationRequestDetailsAction,
  aggregatorCreationRequestDetailsReset as aggregatorCreationRequestDetailsResetAction,
  listLanguages as listLanguagesAction,
  updateAggregatorCreationRequest as updateAggregatorCreationRequestAction,
  updateAggregatorCreationRequestReset as updateAggregatorCreationRequestResetAction,
} from "../../actions";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getStatusErrorMessage } from "../../utils";
import {
  adaptGenders,
  adaptLanguages,
  adaptLegalStatuses,
  adaptOrganizationTypes,
} from "../../utils/adapters";
import { GENDERS, LEGAL_STATUSES, ORGANIZATION_TYPES } from "../../constants";
import {
  adaptErrors,
  adaptInitialAdministrators,
  adaptInitialFromValues,
  adaptValues,
} from "./adapter";
import { AdministratorModal } from "./components/AdministratorModal";
import { Form } from "./components/Form";
import {
  EMPTY_AGGREGATOR_VALUES,
  type AdministratorFormValues,
  type AdministratorRow,
  type AggregatorFormValues,
} from "./schema";
import type {
  AggregatorCreationRequestData,
  AggregatorCreationRequestFieldErrors,
  AggregatorCreationRequestPayload,
  SelectOption,
  ServerError,
} from "./types";

interface RequestState {
  isLoading: boolean;
  error: ServerError;
}

interface RootState {
  listLanguages: {
    isFetching: boolean;
    errorMessage: string;
    data: { results?: unknown[] };
  };
  addAggregatorCreationRequest: RequestState;
  updateAggregatorCreationRequest: RequestState;
}

interface StateProps {
  isFetching: boolean;
  errorMessage: string;
  fieldErrors?: AggregatorCreationRequestFieldErrors;
  legalStatuses: SelectOption[];
  genders: SelectOption[];
  organizationTypes: SelectOption[];
  languages: SelectOption[];
}

interface DispatchProps {
  addAggregatorCreationRequest: (data: AggregatorCreationRequestPayload) => Promise<unknown>;
  addAggregatorCreationRequestReset: () => void;
  listLanguages: () => void;
  updateAggregatorCreationRequest: (
    id: string,
    data: AggregatorCreationRequestPayload
  ) => Promise<unknown>;
  updateAggregatorCreationRequestReset: () => void;
  aggregatorCreationRequestDetails: (
    id: string
  ) => Promise<{ data?: AggregatorCreationRequestData } | undefined>;
  aggregatorCreationRequestDetailsReset: () => void;
}

type RepresentativeAggregatorCreationRequestDetailsProps = StateProps & DispatchProps;

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const LIST_PATH = "/representative/representativeAggregatorCreationRequests";

export const RepresentativeAggregatorCreationRequestDetails = ({
  isFetching,
  errorMessage,
  fieldErrors,
  legalStatuses,
  genders,
  organizationTypes,
  languages,
  addAggregatorCreationRequest,
  addAggregatorCreationRequestReset,
  listLanguages,
  updateAggregatorCreationRequest,
  updateAggregatorCreationRequestReset,
  aggregatorCreationRequestDetails,
  aggregatorCreationRequestDetailsReset,
}: RepresentativeAggregatorCreationRequestDetailsProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();

  const [initialValues, setInitialValues] = useState<AggregatorFormValues>(EMPTY_AGGREGATOR_VALUES);
  const [administrators, setAdministrators] = useState<AdministratorRow[]>([]);
  const [administratorToEdit, setAdministratorToEdit] = useState<AdministratorRow | null>();
  const [administratorToDelete, setAdministratorToDelete] = useState<AdministratorRow | null>(null);

  useEffect(() => {
    if (id) {
      aggregatorCreationRequestDetails(id).then(results => {
        setInitialValues(adaptInitialFromValues(results?.data));
        setAdministrators(adaptInitialAdministrators(results?.data));
      });
    }
    listLanguages();

    return () => {
      addAggregatorCreationRequestReset();
      updateAggregatorCreationRequestReset();
      aggregatorCreationRequestDetailsReset();
    };
  }, [
    addAggregatorCreationRequestReset,
    aggregatorCreationRequestDetails,
    aggregatorCreationRequestDetailsReset,
    id,
    listLanguages,
    updateAggregatorCreationRequestReset,
  ]);

  const onValidate = (formValues: AggregatorFormValues) => {
    const data = adaptValues(formValues, administrators);
    const request = id
      ? updateAggregatorCreationRequest(id, data)
      : addAggregatorCreationRequest(data);

    request.then(result => {
      if (result) {
        history.push(LIST_PATH);
      }
    });
  };

  const onValidateAdministrator = (values: AdministratorFormValues) => {
    setAdministrators(current =>
      administratorToEdit
        ? current.map(administrator =>
            administrator.id === administratorToEdit.id
              ? { ...values, id: administratorToEdit.id }
              : administrator
          )
        : [...current, { ...values, id: uuidv4() }]
    );
    setAdministratorToEdit(undefined);
  };

  const onConfirmDeleteAdministrator = () => {
    setAdministrators(current =>
      current.filter(administrator => administrator.id !== administratorToDelete?.id)
    );
    setAdministratorToDelete(null);
  };

  return (
    <>
      <main className="flex flex-col gap-6 px-6 py-4 sm:px-8 sm:py-6 md:px-10 md:py-8">
        <h1 className="font-semibold text-2xl">
          {t("representativeAggregatorCreationRequestDetails.title")}
        </h1>
        <Form
          isFetching={isFetching}
          errorMessage={errorMessage}
          initialValues={initialValues}
          administrators={administrators}
          legalStatuses={legalStatuses}
          organizationTypes={organizationTypes}
          languages={languages}
          fieldErrors={fieldErrors}
          onValidate={onValidate}
          onClickAddAdministrator={() => setAdministratorToEdit(null)}
          onClickEditAdministrator={setAdministratorToEdit}
          onClickDeleteAdministrator={setAdministratorToDelete}
        />
      </main>

      <AdministratorModal
        isOpen={administratorToEdit !== undefined}
        genders={genders}
        initialValues={administratorToEdit ?? undefined}
        onValidate={onValidateAdministrator}
        onClose={() => setAdministratorToEdit(undefined)}
      />

      <Dialog
        open={Boolean(administratorToDelete)}
        onOpenChange={open => !open && setAdministratorToDelete(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-danger-50 text-danger-600">
                <Trash2 className="size-5" />
              </div>
              <div className="space-y-2">
                <DialogTitle>
                  {t(
                    "representativeAggregatorCreationRequestDetails.form.deleteAdministratorModalTitle"
                  )}
                </DialogTitle>
                <DialogDescription className="text-foreground">
                  {t(
                    "representativeAggregatorCreationRequestDetails.form.deleteAdministratorModalText"
                  )}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <WFPButton
              type="button"
              variant="outline"
              onClick={() => setAdministratorToDelete(null)}
            >
              {t("common.cancel")}
            </WFPButton>
            <WFPButton type="button" variant="destructive" onClick={onConfirmDeleteAdministrator}>
              {t("common.delete")}
            </WFPButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const mapStateToProps = (state: RootState): StateProps => ({
  isFetching:
    state.addAggregatorCreationRequest.isLoading ||
    state.updateAggregatorCreationRequest.isLoading ||
    state.listLanguages.isFetching,
  errorMessage:
    state.listLanguages.errorMessage ||
    getStatusErrorMessage(state.addAggregatorCreationRequest.error) ||
    getStatusErrorMessage(state.updateAggregatorCreationRequest.error) ||
    "",
  fieldErrors:
    adaptErrors(state.addAggregatorCreationRequest.error) ||
    adaptErrors(state.updateAggregatorCreationRequest.error) ||
    undefined,
  legalStatuses: adaptLegalStatuses(LEGAL_STATUSES),
  genders: adaptGenders(GENDERS),
  organizationTypes: adaptOrganizationTypes(ORGANIZATION_TYPES),
  languages: adaptLanguages(state.listLanguages.data.results),
});

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  addAggregatorCreationRequest: data =>
    dispatchAction(
      dispatch,
      addAggregatorCreationRequestAction(data)
    ) as unknown as Promise<unknown>,
  addAggregatorCreationRequestReset: () =>
    dispatchAction(dispatch, addAggregatorCreationRequestResetAction()),
  listLanguages: () => dispatchAction(dispatch, listLanguagesAction()),
  updateAggregatorCreationRequest: (id, data) =>
    dispatchAction(
      dispatch,
      updateAggregatorCreationRequestAction(id, data)
    ) as unknown as Promise<unknown>,
  updateAggregatorCreationRequestReset: () =>
    dispatchAction(dispatch, updateAggregatorCreationRequestResetAction()),
  aggregatorCreationRequestDetails: id =>
    dispatchAction(dispatch, aggregatorCreationRequestDetailsAction(id)) as unknown as Promise<
      { data?: AggregatorCreationRequestData } | undefined
    >,
  aggregatorCreationRequestDetailsReset: () =>
    dispatchAction(dispatch, aggregatorCreationRequestDetailsResetAction()),
});

export const ConnectedRepresentativeAggregatorCreationRequestDetails = connect(
  mapStateToProps,
  mapDispatchToProps
)(RepresentativeAggregatorCreationRequestDetails);
